/**
 * WideTableScroll — Iter 38b (v2 rewrite).
 *
 * Shared wrapper that keeps the horizontal scrollbar of a wide table
 * accessible even when the natural scrollbar would sit far below the
 * viewport because the table is tall.
 *
 * v1 of this component failed in the live browser. Root causes fixed
 * in v2:
 *
 *   1. **Observe the child, not just the wrap.** ResizeObserver on
 *      the wrap div does NOT fire when its child table grows wider
 *      because the wrap's own contentRect is unchanged. v2 observes
 *      both `wrapRef` and its first element child (the table).
 *
 *   2. **Portal the floating bar into document.body.** Any ancestor
 *      with `transform`, `filter`, `backdrop-filter` or `will-change`
 *      establishes a new containing block for `position: fixed`,
 *      breaking viewport-anchored placement. Portalling to body
 *      side-steps every ancestor.
 *
 *   3. **RAF poll for the first ~1500ms after mount.** Table cell
 *      content can lay out asynchronously (web fonts, async data,
 *      chips). A short RAF poll catches the moment overflow becomes
 *      true even when no observable event fires.
 *
 *   4. **Listen on the nearest scrolling ancestor + window.** The
 *      page may scroll on `<body>` (default) or on a nested
 *      container — both must drive `recompute`.
 *
 *   5. **Stable recompute callback.** `useCallback` carries no
 *      dependency on `floatVisible`, so the effect mounts once and
 *      stays stable.
 *
 * Behaviour summary:
 *   • Children render inside the existing `.data-table-wrap` div, so
 *     short tables behave exactly as today.
 *   • A horizontal floating scrollbar is rendered (via portal) at the
 *     viewport bottom, aligned to the table's left edge and matching
 *     its visible width, ONLY when (a) content is wider than the
 *     wrap AND (b) the wrap's natural bottom is below the viewport
 *     fold AND (c) the wrap is on screen at all.
 *   • Two-way `scrollLeft` sync.
 *   • Vertical scroll remains page-level (no nested overflow-y).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

function findScrollingAncestor(el) {
  let p = el && el.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    const cs = window.getComputedStyle(p);
    if (
      (cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
      p.scrollHeight > p.clientHeight
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

export default function WideTableScroll({
  children,
  className = '',
  testId,
  ...rest
}) {
  const wrapRef = useRef(null);
  const floatRef = useRef(null);
  const ancestorScrollerRef = useRef(null);
  const syncingRef = useRef(false);
  const rafIdRef = useRef(null);

  const [floatVisible, setFloatVisible] = useState(false);
  const [floatGeometry, setFloatGeometry] = useState({
    left: 0, width: 0, contentWidth: 0,
  });

  const recompute = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const contentWidth = wrap.scrollWidth;
    const visibleWidth = wrap.clientWidth;
    const hasOverflow = contentWidth > visibleWidth + 1;
    if (!hasOverflow) {
      setFloatVisible((v) => (v ? false : v));
      return;
    }
    const rect = wrap.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const naturalBarBelowFold = rect.bottom > viewportHeight - 2;
    const wrapIntersectsViewport = rect.top < viewportHeight && rect.bottom > 0;
    const shouldFloat = naturalBarBelowFold && wrapIntersectsViewport;
    setFloatVisible((v) => (v === shouldFloat ? v : shouldFloat));
    if (shouldFloat) {
      const left = Math.max(0, rect.left);
      const width = visibleWidth;
      setFloatGeometry((prev) => {
        if (
          prev.left === left &&
          prev.width === width &&
          prev.contentWidth === contentWidth
        ) {
          return prev;
        }
        return { left, width, contentWidth };
      });
    }
  }, []);

  // Mount: wire observers + listeners; tear down on unmount.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    recompute();
    const handlers = [];
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => recompute());
      ro.observe(wrap);
      const child = wrap.firstElementChild;
      if (child) ro.observe(child);
    }
    const onScrollOrResize = () => recompute();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    handlers.push(() => window.removeEventListener('scroll', onScrollOrResize));
    handlers.push(() => window.removeEventListener('resize', onScrollOrResize));
    // Listen to nearest scrolling ancestor (in case the page scrolls
    // on a nested container instead of window).
    const ancestor = findScrollingAncestor(wrap);
    if (ancestor) {
      ancestorScrollerRef.current = ancestor;
      ancestor.addEventListener('scroll', onScrollOrResize, { passive: true });
      handlers.push(() => ancestor.removeEventListener('scroll', onScrollOrResize));
    }
    // RAF poll for the first ~1500ms after mount to catch late layout
    // (web fonts, async row hydration, chip render).
    const start = Date.now();
    const tick = () => {
      recompute();
      if (Date.now() - start < 1500) {
        rafIdRef.current = window.requestAnimationFrame(tick);
      }
    };
    rafIdRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (ro) ro.disconnect();
      handlers.forEach((fn) => fn());
      if (rafIdRef.current) window.cancelAnimationFrame(rafIdRef.current);
      ancestorScrollerRef.current = null;
    };
  }, [recompute]);

  // Two-way scrollLeft sync.
  const handleWrapScroll = useCallback((e) => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    const f = floatRef.current;
    if (f && f.scrollLeft !== e.target.scrollLeft) {
      syncingRef.current = true;
      f.scrollLeft = e.target.scrollLeft;
    }
  }, []);
  const handleFloatScroll = useCallback((e) => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    const w = wrapRef.current;
    if (w && w.scrollLeft !== e.target.scrollLeft) {
      syncingRef.current = true;
      w.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const floatNode = floatVisible ? (
    <div
      ref={floatRef}
      className="wide-table-floating-bar"
      onScroll={handleFloatScroll}
      aria-hidden="true"
      data-testid="wide-table-floating-bar"
      style={{
        position: 'fixed',
        left: floatGeometry.left,
        bottom: 0,
        width: floatGeometry.width,
        height: '14px',
        overflowX: 'auto',
        overflowY: 'hidden',
        zIndex: 40,
        background: 'rgba(255,255,255,0.94)',
        borderTop: '1px solid var(--border)',
        boxShadow: '0 -2px 6px rgba(15, 23, 42, 0.06)',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ width: floatGeometry.contentWidth, height: '1px' }} />
    </div>
  ) : null;

  return (
    <>
      <div
        ref={wrapRef}
        className={`data-table-wrap ${className}`.trim()}
        onScroll={handleWrapScroll}
        data-testid={testId || 'wide-table-scroll'}
        {...rest}
      >
        {children}
      </div>
      {typeof document !== 'undefined' && floatNode
        ? createPortal(floatNode, document.body)
        : floatNode}
    </>
  );
}
